export class GenericDatabase {
    /**
	 * @param {string} dbName - Name of the database
	 * @param {number} version - Version of the database
	 * @param {Array} stores - Array of store definitions: [{ name: 'storeName', keyPath: 'id', indices: [{ name, keyPath, options }] }]
	 * @param {Array} [migrations] - Array of migration objects: [{ fromVersion, toVersion, migrate: (db, tx) => {} }]
	 */
    static instances = []

    constructor (dbName, version, stores, migrations = []) {
        this.dbName = dbName
        this.version = version
        this.stores = stores
        this.migrations = migrations
        this.db = null
        GenericDatabase.instances.push(this)
    }

    close () {
        if (this.db) {
            this.db.close()
            this.db = null
        }
    }

    static closeAll () {
        GenericDatabase.instances.forEach(inst => inst.close())
    }

    init () {
        return new Promise((resolve) => {
            const request = window.indexedDB.open(this.dbName, this.version)

            request.onerror = (event) => {
                resolve({
                    message: 'Error al abrir la base de datos',
                    error: event.target.error,
                    state: false
                })
            }

            request.onsuccess = (event) => {
                this.db = event.target.result
                resolve({
                    message: 'Base de datos abierta',
                    database: this.db,
                    state: 'abierta'
                })
            }

            request.onupgradeneeded = (event) => {
                const db = event.target.result
                const oldVersion = event.oldVersion
                const tx = event.target.transaction

                // Run applicable migrations before creating new stores
                if (this.migrations && this.migrations.length > 0) {
                    this.migrations
                        .filter(
                            (m) =>
                                m.fromVersion === oldVersion && m.toVersion === this.version
                        )
                        .forEach((m) => {
                            m.migrate(db, tx)
                        })
                }

                this.stores.forEach((storeDef) => {
                    let store
                    if (!db.objectStoreNames.contains(storeDef.name)) {
                        store = db.createObjectStore(storeDef.name, {
                            keyPath: storeDef.keyPath
                        })
                    } else {
                        store = event.target.transaction.objectStore(storeDef.name)
                    }

                    if (storeDef.indices) {
                        storeDef.indices.forEach((idx) => {
                            if (!store.indexNames.contains(idx.name)) {
                                store.createIndex(idx.name, idx.keyPath, idx.options)
                            }
                        })
                    }
                })

                event.target.transaction.oncomplete = () => {
                    // Transaction completed
                }
            }
        })
    }

    _getDb (providedDb) {
        return providedDb || this.db
    }

    add (storeName, data, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve(false)
                return
            }
            if (data === null || data === undefined) {
                resolve(false)
                return
            }

            try {
                const tx = db.transaction([storeName], 'readwrite')
                const store = tx.objectStore(storeName)
                const req = store.add(data)

                req.onsuccess = () => resolve(true)
                req.onerror = (e) => {
                    console.error('Error adding data:', e)
                    resolve(false)
                }
            } catch (e) {
                console.error(e)
                resolve(false)
            }
        })
    }

    get (storeName, key, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve(null)
                return
            }

            try {
                const tx = db.transaction([storeName], 'readonly')
                const store = tx.objectStore(storeName)
                const req = store.get(key)

                req.onsuccess = (e) => resolve(e.target.result)
                req.onerror = () => resolve(null)
            } catch (e) {
                console.error(e)
                resolve(null)
            }
        })
    }

    put (storeName, data, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve(false)
                return
            }

            try {
                const tx = db.transaction([storeName], 'readwrite')
                const store = tx.objectStore(storeName)
                const req = store.put(data)

                req.onsuccess = () => resolve(true)
                req.onerror = (e) => {
                    console.error('Error putting data:', e)
                    resolve(false)
                }
            } catch (e) {
                console.error(e)
                resolve(false)
            }
        })
    }

    upsert (storeName, data, providedDb) {
        // Semantic alias for put(): creates or updates a record without throwing on duplicates
        return this.put(storeName, data, providedDb)
    }

    delete (storeName, key, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve(false)
                return
            }

            try {
                const tx = db.transaction([storeName], 'readwrite')
                const store = tx.objectStore(storeName)
                const req = store.delete(key)

                req.onsuccess = () => resolve(true)
                req.onerror = () => resolve(false)
            } catch (e) {
                console.error(e)
                resolve(false)
            }
        })
    }

    getAll (storeName, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve([])
                return
            }

            try {
                const tx = db.transaction([storeName], 'readonly')
                const store = tx.objectStore(storeName)
                const req = store.getAll()

                req.onsuccess = (e) => resolve(e.target.result)
                req.onerror = () => resolve([])
            } catch (e) {
                console.error(e)
                resolve([])
            }
        })
    }

    getByIndex (storeName, indexName, value, providedDb) {
        return new Promise((resolve) => {
            const db = this._getDb(providedDb)
            if (!db) {
                resolve(null)
                return
            }

            try {
                const tx = db.transaction([storeName], 'readonly')
                const store = tx.objectStore(storeName)
                const index = store.index(indexName)
                const req = index.get(value)

                req.onsuccess = (e) => resolve(e.target.result)
                req.onerror = () => resolve(null)
            } catch (e) {
                console.error(e)
                resolve(null)
            }
        })
    }

    deleteDatabase () {
        return new Promise((resolve) => {
            const req = window.indexedDB.deleteDatabase(this.dbName)
            req.onsuccess = () => resolve(true)
            req.onerror = () => resolve(false)
            req.onblocked = () => resolve(false)
        })
    }
}
